/** Text in a y-up page space: every run is flipped locally so it reads upright. */
import type { AppearanceFont } from "../markup/types.js";
import { cssFont } from "./colors.js";
import { type Ctx, type Rect, maxX, midX, midY, maxY, minX, minY } from "./ctx.js";

export function textAt(ctx: Ctx, s: string, x: number, baseline: number): void {
  ctx.save();
  ctx.translate(x, baseline);
  ctx.scale(1, -1);
  ctx.fillText(s, 0, 0);
  ctx.restore();
}

export type Align = "left" | "center" | "right";

/**
 * TakeoffDrawingAnnotation.drawLabel: lines centred vertically on the rect,
 * 1.12 line height, baseline nudged by 0.34 em.
 */
export function drawLabel(
  ctx: Ctx, text: string, r: Rect, color: string,
  opts: { font?: AppearanceFont; fontScale?: number; fontSize?: number; align?: Align; lineHeight?: number } = {},
): void {
  if (!text) return;
  const fontSize = opts.fontSize ?? Math.max(4.5, Math.min(r.w, r.h) * (opts.fontScale ?? (text.includes("\n") ? 0.18 : 0.3)));
  const lines = text.split("\n");
  const lineHeight = fontSize * (opts.lineHeight ?? 1.12);
  const first = midY(r) + ((lines.length - 1) * lineHeight) / 2 - fontSize * 0.34;
  ctx.font = cssFont(opts.font ?? "helveticaBold", fontSize);
  ctx.fillStyle = color;
  const align = opts.align ?? "center";
  lines.forEach((line, i) => {
    const w = ctx.measureText(line).width;
    const x = align === "left" ? minX(r) : align === "right" ? maxX(r) - w : midX(r) - w / 2;
    textAt(ctx, line, x, first - i * lineHeight);
  });
}

/** CustomStamp / fittedFontSize: largest size ≤ preferred at which every line fits. */
export function fittedFontSize(ctx: Ctx, text: string, font: AppearanceFont, preferred: number, r: Rect): number {
  const lines = text.split("\n");
  ctx.font = cssFont(font, 10);
  let widest = 0;
  for (const l of lines) widest = Math.max(widest, ctx.measureText(l).width);
  const byWidth = widest > 0 ? ((r.w * 0.96) / widest) * 10 : preferred;
  const byHeight = r.h / (lines.length * 1.12 + 0.1);
  return Math.max(3, Math.min(preferred, byWidth, byHeight));
}

/** Greedy word wrap at `width`, honouring explicit newlines (Core Text's byWordWrapping). */
export function wrapLines(ctx: Ctx, text: string, width: number): string[] {
  const out: string[] = [];
  for (const paragraph of text.split("\n")) {
    const words = paragraph.split(/(\s+)/);
    let line = "";
    for (const token of words) {
      if (!token) continue;
      const candidate = line + token;
      if (line && /\S/.test(token) && ctx.measureText(candidate.trimEnd()).width > width) {
        out.push(line.trimEnd());
        line = token;
        // A single word wider than the box is broken by characters.
        while (ctx.measureText(line).width > width && line.length > 1) {
          let cut = line.length - 1;
          while (cut > 1 && ctx.measureText(line.slice(0, cut)).width > width) cut--;
          out.push(line.slice(0, cut));
          line = line.slice(cut);
        }
      } else {
        line = candidate;
      }
    }
    out.push(line.trimEnd());
  }
  return out;
}

/** Font metrics Core Text would use (Helvetica-like). */
export const ASCENT = 0.77, DESCENT = 0.23, LEADING = 0;

/** Top-aligned wrapped text inside `r` (CTFrameDraw); lines that don't fit are not drawn. */
export function drawWrapped(ctx: Ctx, text: string, r: Rect, opts: { font: string; size: number; color: string; align: Align }): void {
  if (!text || r.w <= 1 || r.h <= 1) return;
  ctx.font = opts.font;
  ctx.fillStyle = opts.color;
  const lineHeight = opts.size * (ASCENT + DESCENT + LEADING);
  const lines = wrapLines(ctx, text, r.w);
  let baseline = maxY(r) - opts.size * ASCENT;
  for (const line of lines) {
    if (baseline - opts.size * DESCENT < minY(r) - 0.5) break;
    const w = ctx.measureText(line).width;
    const x = opts.align === "left" ? minX(r) : opts.align === "right" ? maxX(r) - w : midX(r) - w / 2;
    if (line) textAt(ctx, line, x, baseline);
    baseline -= lineHeight;
  }
}
